import React, { Component } from "react";
import { Link } from "react-router-dom";
import "animate.css";

import Navbar from "../Navbar/Navbar";
import MainBodyContainer from "../Common/Body/MainBodyContainer/MainBodyContainer";
import Footer from "../Footer/Footer";
import { EmploymentSupportData } from "../Data/EmploymentSupportData";

import "./EmploymentSupport.css";

class EmploymentSupport extends Component {
  successMetrics = [
    {
      number: "45.7%",
      label: "Employment Success Rate",
      description: "Of active job seekers secured employment",
    },
    {
      number: "16",
      label: "Jobs Secured",
      description: "In 2024-25 (from 35 active seekers)",
    },
    {
      number: "200+",
      label: "CVs Reviewed",
      description: "Professional guidance annually",
    },
    {
      number: "Free",
      label: "All Services",
      description: "For community members",
    },
  ];

  render() {
    // Filter out success stories from main services
    const mainServices = EmploymentSupportData.filter(
      (service) => service.id !== "success-stories"
    );
    const successStoriesData = EmploymentSupportData.find(
      (service) => service.id === "success-stories"
    );

    return (
      <div>
        <Navbar />
        <div>
          <MainBodyContainer heading={"Local Employment Support"}>
            {/* Hero Section */}
            <div className="employment-hero">
              <div className="hero-content">
                <h1 className="animate__animated animate__fadeInUp">
                  Your Path To Employment
                </h1>
                <p className="hero-subtitle animate__animated animate__fadeInUp">
                  CV Support, Job Search, Skills Assessment, And Real Success Stories
                </p>
                <p className="hero-description">
                  Finding employment is about more than just having the right skills - it's about 
                  showcasing your unique strengths, understanding the UK job market, and having someone 
                  in your corner. We provide personalized, one-to-one support that honors your 
                  circumstances and helps you find not just any job, but the right opportunity for you.
                </p>

                {/* Image Placeholder */}
                <div className="hero-image-placeholder">
                  <div className="placeholder-content">
                    <p>📸 Image Suggestion: Professional setting showing a one-to-one CV review session. 
                    Employment advisor (diverse background) working with a job seeker at a desk with laptop 
                    and documents. Warm, supportive atmosphere. Natural office lighting.</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Success Metrics */}
            <div className="metrics-section">
              <h2 className="section-header centered">Our Track Record</h2>
              <p className="metrics-intro">
                These aren't just numbers - they represent real people who've overcome real barriers 
                to secure meaningful employment
              </p>
              <div className="metrics-grid">
                {this.successMetrics.map((metric, index) => (
                  <div key={index} className="metric-card">
                    <h2 className="metric-number">{metric.number}</h2>
                    <h3 className="metric-label">{metric.label}</h3>
                    <p className="metric-description">{metric.description}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Services Overview */}
            <div className="services-section">
              <div className="section-intro">
                <h2 className="section-header">How We Support You</h2>
                <p className="section-description">
                  From your first CV to your first day at work, we're with you at every step. 
                  All services are free, flexible, and available in multiple languages.
                </p>
              </div>

              <div className="services-grid">
                {mainServices.map((service, index) => (
                  <div key={index} className="service-card">
                    {/* Image Placeholder for Each Service */}
                    <div className="service-image-placeholder">
                      <div className="placeholder-text">
                        {service.id === "cv-interview-support" && 
                          "📸 Close-up of hands reviewing CV document with pen, advisor pointing at text"}
                        {service.id === "job-search-assistance" && 
                          "📸 Person using computer with job search websites visible on screen"}
                        {service.id === "skills-assessment" && 
                          "📸 Career counseling session - two people discussing career pathway chart/documents"}
                      </div>
                    </div>

                    <div className="service-card-content">
                      <h3 className="service-title">{service.title}</h3>
                      <p className="service-short-description">
                        {service.shortDescription}
                      </p>
                      <p className="service-description">{service.description}</p>

                      <div className="service-highlights">
                        <h4>Who This Helps:</h4>
                        <ul className="help-list">
                          {service.whoThisHelps.slice(0, 4).map((help, i) => (
                            <li key={i}>{help}</li>
                          ))}
                        </ul>
                      </div>

                      <div className="service-offerings">
                        <h4>What We Offer:</h4>
                        <div className="offerings-list">
                          {service.whatWeOffer.slice(0, 3).map((offering, i) => (
                            <div key={i} className="offering-item">
                              <span className="offering-icon">→</span>
                              <span className="offering-name">{offering.service}</span>
                            </div>
                          ))}
                        </div>
                      </div>

                      <Link 
                        to={`/employment-support/${service.id}`} 
                        className="service-learn-more"
                      >
                        Learn More & Book Support
                        <span className="arrow">→</span>
                      </Link>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Why Our Approach Works */}
            <div className="approach-section">
              <h2 className="section-header centered">Why Our Approach Works</h2>
              <div className="approach-grid">
                <div className="approach-card">
                  <div className="approach-icon">🤝</div>
                  <h3>One-to-One Support</h3>
                  <p>
                    Not group sessions or online forms - real conversations with advisors who 
                    understand your unique circumstances and barriers
                  </p>
                </div>
                <div className="approach-card">
                  <div className="approach-icon">🌍</div>
                  <h3>Cultural Understanding</h3>
                  <p>
                    Multilingual staff and advisors who understand international qualifications, 
                    immigration status, and non-traditional career paths
                  </p>
                </div>
                <div className="approach-card">
                  <div className="approach-icon">💼</div>
                  <h3>Employer Connections</h3>
                  <p>
                    Direct relationships with local employers actively hiring - including those 
                    offering flexible hours and supporting diverse candidates
                  </p>
                </div>
                <div className="approach-card">
                  <div className="approach-icon">🔄</div>
                  <h3>Holistic Support</h3>
                  <p>
                    We address real barriers - childcare, transport costs, digital access, 
                    confidence - not just CV formatting
                  </p>
                </div>
                <div className="approach-card">
                  <div className="approach-icon">📈</div>
                  <h3>Long-Term Partnership</h3>
                  <p>
                    Support doesn't end when you get a job - we follow up, help with workplace 
                    challenges, and support career progression
                  </p>
                </div>
                <div className="approach-card">
                  <div className="approach-icon">⚖️</div>
                  <h3>Rights Advocacy</h3>
                  <p>
                    We ensure you understand employment rights, challenge discrimination, and 
                    connect to legal support when needed
                  </p>
                </div>
              </div>
            </div>

            {/* Success Story Spotlight */}
            {successStoriesData && successStoriesData.categories && (
              <div className="success-spotlight-section">
                <h2 className="section-header centered">Success Stories From Your Neighbors</h2>
                <p className="spotlight-intro">
                  These are real journeys from community members who found their path with APT support
                </p>
                
                <div className="spotlight-grid">
                  {successStoriesData.categories.slice(0, 3).map((category, index) => (
                    <div key={index} className="spotlight-card">
                      <h3 className="spotlight-category">{category.category}</h3>
                      {category.stories.slice(0, 1).map((story, i) => (
                        <div key={i} className="story-preview">
                          <h4 className="story-name">{story.name}</h4>
                          <p className="story-before">
                            <strong>Before:</strong> {story.before}
                          </p>
                          <p className="story-after">
                            <strong>After:</strong> {story.after}
                          </p>
                          <p className="story-quote">"{story.quote}"</p>
                          <p className="story-timeline">Journey: {story.timeline}</p>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>

                <div className="spotlight-cta">
                  <Link to="/employment-support/success-stories" className="success-stories-link">
                    Read More Success Stories
                    <span className="arrow">→</span>
                  </Link>
                </div>
              </div>
            )}

            {/* How to Access Section */}
            <div className="access-section">
              <div className="access-content">
                <div className="access-text">
                  <h2 className="section-header">Ready To Start Your Job Search?</h2>
                  <p className="access-description">
                    Don't navigate the job market alone. Our support is free, confidential, and 
                    designed around your needs - not bureaucratic requirements.
                  </p>
                  
                  <div className="access-options">
                    <div className="access-option">
                      <span className="option-icon">📞</span>
                      <div className="option-text">
                        <h4>Call Us</h4>
                        <p>Speak to an advisor for immediate guidance</p>
                      </div>
                    </div>
                    <div className="access-option">
                      <span className="option-icon">🚪</span>
                      <div className="option-text">
                        <h4>Drop-In Sessions</h4>
                        <p>Tuesdays & Thursdays, 10am-2pm - No appointment needed</p>
                      </div>
                    </div>
                    <div className="access-option">
                      <span className="option-icon">💬</span>
                      <div className="option-text">
                        <h4>Multilingual Support</h4>
                        <p>Support available in Arabic, Albanian, Bengali, Urdu, Tigrinya</p>
                      </div>
                    </div>
                  </div>

                  <div className="access-buttons">
                    <Link to="/contact" className="access-button primary">
                      Get Started Today
                    </Link>
                    <Link to="/training" className="access-button secondary">
                      Explore Training Courses
                    </Link>
                  </div>
                </div>

                <div className="access-image-placeholder">
                  <div className="placeholder-content">
                    <p>📸 Image Suggestion: Welcoming office reception area with diverse staff member 
                    greeting a visitor. Bright, accessible space with multilingual signage visible. 
                    Professional but warm atmosphere.</p>
                  </div>
                </div>
              </div>
            </div>
          </MainBodyContainer>
        </div>
        <Footer />
      </div>
    );
  }
}

export default EmploymentSupport;
